// Slice 2C2B · Document Defaults draft-storage helper.
//
// Responsibilities:
//   * Deterministic per-user, per-family localStorage key construction.
//   * Safe read/write/remove that survives storage unavailability, quota
//     errors, JSON corruption, cross-user leakage, and schema drift.
//   * Baseline fingerprint + field-level equality helpers so callers can
//     detect “unsaved changes” and stale drafts without importing lodash
//     or hand-rolling comparisons inside the shared family card.
//
// This module is intentionally the ONLY place that touches localStorage
// for document-defaults drafts, so all invariants (namespace, schema,
// ownership) live in one file. Slice 2C2A and older slices remain
// untouched.

// ---------------------------------------------------------------------------
// Namespace + schema
// ---------------------------------------------------------------------------
// Bump SCHEMA_VERSION whenever the stored payload shape changes in a way
// that must NOT be silently rehydrated by older code. Older-schema drafts
// are dropped on read.
export const DRAFT_SCHEMA_VERSION = 1;
export const DRAFT_KEY_PREFIX = "jobclose:document-defaults:draft";

export const KNOWN_FAMILY_KEYS = [
  "proposal",
  "contract",
  "invoice",
  "customer_update",
  "closeout",
];

// Build the versioned, user-scoped, family-scoped localStorage key.
export const buildDraftKey = (userId, familyKey) => {
  if (!userId || !familyKey) return null;
  return `${DRAFT_KEY_PREFIX}:v${DRAFT_SCHEMA_VERSION}:${userId}:${familyKey}`;
};

// Parse a key back into { userId, familyKey } for storage-event routing.
// Returns null on any mismatch, foreign namespace, or wrong schema.
export const parseDraftKey = (key) => {
  if (!key || typeof key !== "string") return null;
  const expectedPrefix = `${DRAFT_KEY_PREFIX}:v${DRAFT_SCHEMA_VERSION}:`;
  if (!key.startsWith(expectedPrefix)) return null;
  const rest = key.slice(expectedPrefix.length);
  const lastColon = rest.lastIndexOf(":");
  if (lastColon <= 0) return null;
  const userId = rest.slice(0, lastColon);
  const familyKey = rest.slice(lastColon + 1);
  if (!KNOWN_FAMILY_KEYS.includes(familyKey)) return null;
  return { userId, familyKey };
};

// ---------------------------------------------------------------------------
// Storage availability
// ---------------------------------------------------------------------------
// localStorage may be entirely blocked (Safari private mode historically,
// enterprise policies, security extensions) or intermittently blocked
// (quota exceeded). Callers use this at mount time to decide whether to
// show the “backup unavailable” warning + beforeunload protection.
export const isStorageAvailable = () => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return false;
    const probe = "__jc_probe__";
    window.localStorage.setItem(probe, "1");
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
};

// ---------------------------------------------------------------------------
// Field extraction + fingerprint
// ---------------------------------------------------------------------------
// Pull ONLY this family’s configured field keys out of a broader values
// object. Explicit empty strings are preserved.
export const pickFamilyValues = (values, fieldKeys) => {
  const out = {};
  for (const k of fieldKeys) {
    const v = values && Object.prototype.hasOwnProperty.call(values, k) ? values[k] : "";
    out[k] = typeof v === "string" ? v : v == null ? "" : String(v);
  }
  return out;
};

// Deterministic, collision-safe fingerprint of a family's values.
//
// Uses JSON.stringify over sorted [key, value] pairs so that user-entered
// characters — pipe (|), equals (=), quotation marks, newlines, Unicode,
// Spanish diacritics, and JSON-looking text — can NEVER produce false
// equality between distinct value sets. `key=value|key=value` was NOT
// collision-safe (e.g. `a=b|c` collided with `a=b`, `c=`). Object
// insertion order is normalized by sorting the key list up front.
//
// Placeholder attributes are excluded because pickFamilyValues only pulls
// the configured field keys, and placeholder is a UI-layer attribute
// held elsewhere.
export const computeFingerprint = (values, fieldKeys) => {
  const picked = pickFamilyValues(values, fieldKeys);
  const sortedKeys = [...fieldKeys].sort();
  const pairs = sortedKeys.map((k) => [k, String(picked[k] ?? "")]);
  return JSON.stringify(pairs);
};

// Strict field-by-field equality with explicit empty-string handling.
export const valuesEqual = (a, b, fieldKeys) => {
  for (const k of fieldKeys) {
    const av = a && Object.prototype.hasOwnProperty.call(a, k) ? a[k] : "";
    const bv = b && Object.prototype.hasOwnProperty.call(b, k) ? b[k] : "";
    if ((av ?? "") !== (bv ?? "")) return false;
  }
  return true;
};

// ---------------------------------------------------------------------------
// Read / write / remove
// ---------------------------------------------------------------------------

// Read a family draft. Returns null when:
//   * localStorage is unavailable
//   * no record exists
//   * JSON parse fails
//   * schemaVersion doesn't match
//   * userId does not match (defends against another user's stale drafts
//     that survived a logout/login cycle in the same browser profile)
//   * familyKey doesn't match
//   * values is not an object
//
// Slice 2C2B correction: when the record stored at THIS caller's exact
// (userId, familyKey) key is invalid, we self-heal by removing ONLY that
// exact key. No other user's key is ever scanned or touched here.
export const readDraft = (userId, familyKey) => {
  const key = buildDraftKey(userId, familyKey);
  if (!key) return null;
  const removeSelf = () => {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.removeItem(key);
      }
    } catch {
      /* swallow */
    }
  };
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      removeSelf();
      return null;
    }
    if (!parsed || typeof parsed !== "object") { removeSelf(); return null; }
    if (parsed.schemaVersion !== DRAFT_SCHEMA_VERSION) { removeSelf(); return null; }
    if (parsed.userId !== userId) { removeSelf(); return null; }
    if (parsed.familyKey !== familyKey) { removeSelf(); return null; }
    if (!parsed.values || typeof parsed.values !== "object") { removeSelf(); return null; }
    return {
      schemaVersion: parsed.schemaVersion,
      userId: parsed.userId,
      familyKey: parsed.familyKey,
      values: parsed.values,
      baselineFingerprint: parsed.baselineFingerprint || "",
      baselineUpdatedAt: parsed.baselineUpdatedAt || "",
      editedAt: parsed.editedAt || "",
    };
  } catch {
    return null;
  }
};

// Write a family draft. Returns the newly generated editedAt ISO string
// on success (so callers can track the “newest known” timestamp for
// multi-tab conflict detection), or `false` on ANY storage failure
// (unavailable, quota, blocked). Never throws.
export const writeDraft = (userId, familyKey, { values, fieldKeys, baselineFingerprint, baselineUpdatedAt }) => {
  const key = buildDraftKey(userId, familyKey);
  if (!key) return false;
  const familyValues = pickFamilyValues(values, fieldKeys);
  const editedAt = new Date().toISOString();
  const payload = {
    schemaVersion: DRAFT_SCHEMA_VERSION,
    userId,
    familyKey,
    values: familyValues,
    baselineFingerprint: baselineFingerprint || "",
    baselineUpdatedAt: baselineUpdatedAt || "",
    editedAt,
  };
  try {
    if (typeof window === "undefined" || !window.localStorage) return false;
    window.localStorage.setItem(key, JSON.stringify(payload));
    return editedAt;
  } catch {
    return false;
  }
};

// Remove a family draft. Never throws.
export const removeDraft = (userId, familyKey) => {
  const key = buildDraftKey(userId, familyKey);
  if (!key) return;
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.removeItem(key);
  } catch {
    /* swallow */
  }
};

// Enumerate all draft keys the current user could own — used by parent
// pages that want to sweep foreign-user leftovers on mount.
export const listUserDraftKeys = (userId) => {
  if (!userId) return [];
  return KNOWN_FAMILY_KEYS.map((f) => buildDraftKey(userId, f));
};

// Slice 2C2B correction: cross-user isolation is achieved by IGNORING
// foreign drafts on read (readDraft returns null for any record whose
// stored userId doesn't match the caller), not by scanning and deleting
// them at mount. Signing in as user B must NEVER destroy user A's local
// drafts — user A must be able to sign back in later and restore.
//
// The only cleanup we perform is at the EXACT current-user/current-family
// key when its stored contents fail validation (bad JSON, wrong schema,
// mismatched user/family key, wrong shape). That self-heal happens
// inline inside readDraft(). No broad scan exists.
//
// Exposed as a no-op-safe helper so parent pages can call it explicitly
// if they want to prime a family key check without hydrating a value.
export const sweepInvalidOwnDraft = (userId, familyKey) => {
  // readDraft self-heals on its own; calling it and discarding the
  // return achieves the desired cleanup with zero side-effects on other
  // users' or families' keys.
  readDraft(userId, familyKey);
};

// Small helper so tests can assert we never persist auth tokens or full
// API responses. Exported for reuse by scripts.
export const draftPayloadShapeSample = () => ({
  schemaVersion: DRAFT_SCHEMA_VERSION,
  userId: "u",
  familyKey: "proposal",
  values: {},
  baselineFingerprint: "",
  baselineUpdatedAt: "",
  editedAt: "",
});
